import React, { useState, useEffect, useCallback } from 'react';
import { customerAPI, userAPI } from '../services/api';
import CustomerModal from './CustomerModal/CustomerModal';
import ProjectModal from './ProjectModal/ProjectModal';
import { contactDisplayName } from '../utils/contactName';
import { formatPhone } from '../utils/phone';
import { useAuth } from '../AuthContext';

// Mirror of ContactType enum on the backend. The order here drives the
// chip-row order and the badge labels. Type=int in API responses
// (no JsonStringEnumConverter is registered), so we key by number.
const CONTACT_TYPES = [
    { value: 0, name: 'Homeowner', label: 'Homeowner' },
    { value: 1, name: 'PropertyManager', label: 'Property Manager' },
    { value: 2, name: 'Subcontractor', label: 'Subcontractor' },
    { value: 3, name: 'Supplier', label: 'Supplier' },
    { value: 4, name: 'Inspector', label: 'Inspector' },
    { value: 5, name: 'Adjuster', label: 'Adjuster' },
    { value: 6, name: 'Other', label: 'Other' },
    { value: 7, name: 'Tenant', label: 'Tenant' },
];

// Mirrors ContactStatus on the backend. Archived contacts stay in the table -
// deleting one cascades to their projects and tasks, which is the wrong outcome
// for "this property manager moved companies".
const STATUS_ARCHIVED = 5;

const typeLabel = (value) =>
    CONTACT_TYPES.find((t) => t.value === value)?.label ?? 'Unknown';

const CustomerDashboard = ({ onViewCustomer }) => {
    // Deleting a contact cascades to its projects and their S3 documents, so it is
    // admin-only. Everyone keeps Archive, which is the reversible version.
    const { user: currentUser } = useAuth();
    const isAdmin = currentUser?.role?.toLowerCase() === 'admin';

    const [customers, setCustomers] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingCustomer, setEditingCustomer] = useState(null);
    // Held in state (rather than built inline in the JSX) so their identity is
    // stable — both modals re-seed their forms whenever the seed object changes.
    const [duplicatingCustomer, setDuplicatingCustomer] = useState(null);
    const [projectSeed, setProjectSeed] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    // null = "All types"; otherwise an enum value 0..6.
    const [typeFilter, setTypeFilter] = useState(null);
    // 'all' or a positive integer. Client-side pagination over filteredCustomers.
    const [pageSize, setPageSize] = useState(50);
    const [showArchived, setShowArchived] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);

    const fetchCustomers = useCallback(async () => {
        try {
            setLoading(true);
            const params = { limit: 1000 };
            if (typeFilter !== null) {
                const match = CONTACT_TYPES.find((t) => t.value === typeFilter);
                if (match) params.type = match.name;
            }
            const response = await customerAPI.getAll(params);
            setCustomers(response.data);
        } catch (error) {
            console.error('Error fetching contacts:', error);
        } finally {
            setLoading(false);
        }
    }, [typeFilter]);

    useEffect(() => {
        fetchCustomers();
    }, [fetchCustomers]);

    // Only needed to populate the sales-rep picker when adding a project from a
    // row. Not fatal if it fails — the picker is optional.
    useEffect(() => {
        userAPI.getAll()
            .then((res) => setUsers(res.data || []))
            .catch((error) => console.error('Error loading users:', error));
    }, []);

    const handleEdit = (customer) => {
        setEditingCustomer(customer);
        setShowEditModal(true);
    };

    // Opens the create form pre-filled from this contact rather than copying it
    // straight away — a duplicate is nearly always "same company, different
    // person/unit", so it needs an edit before it's saved.
    const handleDuplicate = (customer) => {
        setDuplicatingCustomer(customer);
    };

    // The project is fixed to this contact, so the modal gets a seeded picker and
    // an options list of exactly one.
    const handleAddProject = (customer) => {
        setProjectSeed({ customerId: customer.id, customer });
    };

    const handleArchiveToggle = async (customer) => {
        const archiving = customer.status !== STATUS_ARCHIVED;
        try {
            // PUT expects the whole record; spread so nothing else is nulled out.
            await customerAPI.update(customer.id, {
                ...customer,
                status: archiving ? STATUS_ARCHIVED : 0,
            });
            fetchCustomers();
        } catch (error) {
            console.error('Error updating contact status:', error);
            alert(archiving ? 'Failed to archive contact.' : 'Failed to restore contact.');
        }
    };

    const handleDelete = async (customerId) => {
        if (window.confirm('Are you sure you want to delete this contact? This will also delete all associated projects and tasks.')) {
            try {
                await customerAPI.delete(customerId);
                fetchCustomers();
            } catch (error) {
                console.error('Error deleting contact:', error);
                alert('Failed to delete contact.');
            }
        }
    };

    // Local search across the primary identifying fields. All field accesses
    // are null-safe — imported PM/Sub rows often have null email/phone.
    const search = searchTerm.trim().toLowerCase();
    const visibleCustomers = showArchived
        ? customers
        : customers.filter((c) => c.status !== STATUS_ARCHIVED);

    const filteredCustomers = !search
        ? visibleCustomers
        : visibleCustomers.filter((c) =>
              [
                  c.firstName,
                  c.lastName,
                  c.displayName,
                  c.companyName,
                  c.email,
                  c.phone,
                  c.mainPhone,
                  c.mobilePhone,
                  c.workPhone,
              ]
                  .filter(Boolean)
                  .some((v) => v.toLowerCase().includes(search))
          );

    // Reset to page 1 whenever the visible set might shift under the user.
    useEffect(() => {
        setCurrentPage(1);
    }, [typeFilter, searchTerm, pageSize, showArchived]);

    // Pagination math. pageSize === 'all' means no slicing.
    const showAll = pageSize === 'all';
    const totalPages = showAll ? 1 : Math.max(1, Math.ceil(filteredCustomers.length / pageSize));
    const safePage = Math.min(Math.max(1, currentPage), totalPages);
    const startIdx = showAll ? 0 : (safePage - 1) * pageSize;
    const endIdx = showAll ? filteredCustomers.length : Math.min(startIdx + pageSize, filteredCustomers.length);
    const paginatedCustomers = filteredCustomers.slice(startIdx, endIdx);
    const showingFrom = filteredCustomers.length === 0 ? 0 : startIdx + 1;
    const showingTo = endIdx;

    if (loading) {
        return <div className="loading">Loading contacts...</div>;
    }

    return (
        <div className="customer-dashboard">
            <div className="dashboard-header">
                <div className="header-left">
                    <h2 className="dashboard-title">Contacts</h2>
                    <span className="customer-count">({filteredCustomers.length})</span>
                </div>

                <div className="header-actions">
                    <div className="search-box">
                        <input
                            type="text"
                            placeholder="Search contacts..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="search-input"
                        />
                        <i className="fas fa-search search-icon"></i>
                    </div>

                    <button
                        className="btn-create-customer"
                        onClick={() => setShowCreateModal(true)}
                    >
                        + Add Contact
                    </button>
                </div>
            </div>

            {/* Type filter chips — null = All. */}
            <div className="contact-type-chips">
                <button
                    type="button"
                    className={`type-chip ${typeFilter === null ? 'active' : ''}`}
                    onClick={() => setTypeFilter(null)}
                >
                    All
                </button>
                {CONTACT_TYPES.map((t) => (
                    <button
                        key={t.value}
                        type="button"
                        className={`type-chip ${typeFilter === t.value ? 'active' : ''}`}
                        onClick={() => setTypeFilter(t.value)}
                    >
                        {t.label}
                    </button>
                ))}

                {/* Sits apart from the type chips because it stacks with them
                    rather than replacing the selection. */}
                <button
                    type="button"
                    className={`type-chip type-chip-archived ${showArchived ? 'active' : ''}`}
                    onClick={() => setShowArchived((v) => !v)}
                    title={showArchived ? 'Hide archived contacts' : 'Show archived contacts'}
                >
                    <i className="fas fa-box-archive"></i> Archived
                </button>
            </div>

            {filteredCustomers.length === 0 ? (
                <div className="empty-state">
                    <h3>No contacts found</h3>
                    <p>{searchTerm ? 'Try adjusting your search terms.' : 'Add your first contact to get started!'}</p>
                </div>
            ) : (
                <div className="customers-table-container">
                    <table className="customers-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Type</th>
                                <th>Contact</th>
                                <th>Address</th>
                                <th>Projects</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedCustomers.map((customer) => {
                                const primaryPhone =
                                    customer.phone ||
                                    customer.mainPhone ||
                                    customer.mobilePhone ||
                                    customer.workPhone ||
                                    '';
                                // Prefer property address (where the work is); fall back to
                                // billing for homeowners that only filled the latter.
                                const street = customer.propertyStreet || customer.billingStreet;
                                const city = customer.propertyCity || customer.billingCity;
                                const state = customer.propertyState || customer.billingState;
                                const zip = customer.propertyZipCode || customer.billingZipCode;
                                return (
                                    <tr key={customer.id} className="customer-row">
                                        <td className="customer-name">
                                            <div
                                                className="name-primary customer-link"
                                                onClick={() => onViewCustomer && onViewCustomer(customer.id)}
                                                role="button"
                                                tabIndex={0}
                                                onKeyDown={(e) =>
                                                    e.key === 'Enter' && onViewCustomer && onViewCustomer(customer.id)
                                                }
                                            >
                                                {contactDisplayName(customer)}
                                            </div>
                                            {customer.companyName &&
                                                customer.companyName !== contactDisplayName(customer) && (
                                                    <div className="company-name">{customer.companyName}</div>
                                                )}
                                        </td>
                                        <td className="contact-type-cell" data-label="Type">
                                            <span className={`contact-type-badge type-${customer.type}`}>
                                                {typeLabel(customer.type)}
                                            </span>
                                        </td>
                                        <td className="contact-info" data-label="Contact">
                                            {customer.email && <div className="email">{customer.email}</div>}
                                            {primaryPhone && <div className="phone">{formatPhone(primaryPhone)}</div>}
                                            {!customer.email && !primaryPhone && (
                                                <div className="phone" style={{ color: 'var(--text-muted, #888)' }}>
                                                    —
                                                </div>
                                            )}
                                        </td>
                                        <td className="address-info" data-label="Address">
                                            {street ? (
                                                <>
                                                    <div className="address-line">
                                                        {street}
                                                        {city ? `, ${city}` : ''}
                                                    </div>
                                                    {(state || zip) && (
                                                        <div className="state-zip">
                                                            {state} {zip}
                                                        </div>
                                                    )}
                                                </>
                                            ) : (
                                                <span style={{ color: 'var(--text-muted, #888)' }}>—</span>
                                            )}
                                        </td>
                                        <td className="projects-count" data-label="Projects">
                                            <span className="count-badge">
                                                {customer.projects?.length || 0} Projects
                                            </span>
                                        </td>
                                        <td className="customer-actions" data-label="Actions">
                                            <div className="action-buttons">
                                                <button
                                                    className="action-btn btn-add-project"
                                                    onClick={() => handleAddProject(customer)}
                                                    title="Add project for this contact"
                                                >
                                                    <i className="fas fa-folder-plus"></i>
                                                </button>

                                                <button
                                                    className="action-btn btn-edit"
                                                    onClick={() => handleEdit(customer)}
                                                    title="Edit contact"
                                                >
                                                    <i className="fas fa-edit"></i>
                                                </button>

                                                <button
                                                    className="action-btn btn-duplicate"
                                                    onClick={() => handleDuplicate(customer)}
                                                    title="Duplicate contact"
                                                >
                                                    <i className="fas fa-copy"></i>
                                                </button>

                                                <button
                                                    className="action-btn btn-archive"
                                                    onClick={() => handleArchiveToggle(customer)}
                                                    title={customer.status === STATUS_ARCHIVED ? 'Restore contact' : 'Archive contact'}
                                                >
                                                    <i className={customer.status === STATUS_ARCHIVED ? 'fas fa-box-open' : 'fas fa-box-archive'}></i>
                                                </button>

                                                {/* Admins only, matching the endpoint. Hidden rather
                                                    than disabled: the button is next to Archive,
                                                    which everyone can use, and a greyed-out trash
                                                    icon invites a support question instead of
                                                    pointing at the thing to use instead. */}
                                                {isAdmin && (
                                                    <button
                                                        className="action-btn btn-delete"
                                                        onClick={() => handleDelete(customer.id)}
                                                        title="Delete contact"
                                                    >
                                                        <i className="fas fa-trash-alt"></i>
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>

                    {/* Pagination controls — only show when there's something to paginate
                        and not when the user has selected "All". */}
                    <div className="pagination-bar">
                        <div className="pagination-size">
                            <label htmlFor="pageSize">Show</label>
                            <select
                                id="pageSize"
                                value={pageSize}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setPageSize(v === 'all' ? 'all' : parseInt(v, 10));
                                }}
                            >
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                                <option value={250}>250</option>
                                <option value="all">All</option>
                            </select>
                            <span>per page</span>
                        </div>

                        <div className="pagination-summary">
                            {filteredCustomers.length === 0
                                ? '0 contacts'
                                : `${showingFrom}–${showingTo} of ${filteredCustomers.length}`}
                        </div>

                        {!showAll && totalPages > 1 && (
                            <div className="pagination-nav">
                                <button
                                    type="button"
                                    className="pagination-btn"
                                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                    disabled={safePage <= 1}
                                    title="Previous page"
                                >
                                    <i className="fas fa-chevron-left"></i>
                                </button>
                                <span className="pagination-page-info">
                                    Page {safePage} of {totalPages}
                                </span>
                                <button
                                    type="button"
                                    className="pagination-btn"
                                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                                    disabled={safePage >= totalPages}
                                    title="Next page"
                                >
                                    <i className="fas fa-chevron-right"></i>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Create Contact Modal */}
            <CustomerModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onCustomerCreated={fetchCustomers}
            />

            {/* Edit Contact Modal */}
            <CustomerModal
                isOpen={showEditModal}
                onClose={() => {
                    setShowEditModal(false);
                    setEditingCustomer(null);
                }}
                onCustomerCreated={fetchCustomers}
                editingCustomer={editingCustomer}
            />

            {/* Duplicate Contact Modal — creates, seeded from an existing row. */}
            {duplicatingCustomer && (
                <CustomerModal
                    isOpen={true}
                    onClose={() => setDuplicatingCustomer(null)}
                    onCustomerCreated={fetchCustomers}
                    seedCustomer={duplicatingCustomer}
                />
            )}

            {/* Add Project for a contact, straight off its row.

                customers is the whole list, not just the row that was clicked. Passing
                a single-element array left the modal's contact search with exactly one
                thing to find, so Add Project opened from a contact row could not reach
                any other contact — while the same modal opened from the homepage worked,
                because that path hands it the full list. The seed still selects the
                clicked contact; it just no longer hides every other one. */}
            {projectSeed && (
                <ProjectModal
                    isOpen={true}
                    onClose={() => setProjectSeed(null)}
                    onProjectCreated={fetchCustomers}
                    seedProject={projectSeed}
                    customers={customers}
                    users={users}
                />
            )}
        </div>
    );
};

export default CustomerDashboard;
